import React, { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelSelect from 'src/modules/shared/components/atoms/inputs/label-select';
import type { User } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PageFiltersBar } from 'src/modules/shared/components/molecules/page-filters-bar';
import { CALL_SEARCH_MAX_LENGTH } from '../../constants/call-search.constant';
import { normalizeCallSearch } from '../../utils/call-search.util';

const SEARCH_DEBOUNCE_MS = 500;

interface CallFiltersProps {
  search: string;
  searchError?: string;
  agentId?: number;
  agents: User[];
  agentsLoading: boolean;
  onSearchChange: (value: string) => void;
  onAgentChange: (value?: number) => void;
}

export const CallFilters: React.FC<CallFiltersProps> = ({
  search,
  searchError,
  agentId,
  agents,
  agentsLoading,
  onSearchChange,
  onAgentChange,
}) => {
  const [localSearch, setLocalSearch] = useState(search);
  const debouncedSearch = useDebounce(localSearch, SEARCH_DEBOUNCE_MS);
  const onSearchChangeRef = useRef(onSearchChange);

  const handleAgentChange = (value: string): void => {
    if (!value) {
      onAgentChange(undefined);
      return;
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
      onAgentChange(undefined);
      return;
    }

    onAgentChange(parsed);
  };

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  useEffect(() => {
    onSearchChangeRef.current(debouncedSearch);
  }, [debouncedSearch]);

  const agentOptions = [
    // TODO: check when implementing i18n
    { value: '', content: 'Todos los agentes' },
    ...agents.map((agent) => ({
      value: agent.id.toString(),
      content:
        `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim() ||
        // TODO: check when implementing i18n
        'Sin nombre',
    })),
  ];
  const normalizedSearchLength = normalizeCallSearch(localSearch).length;
  const showCharacterCount =
    normalizedSearchLength >= Math.floor(CALL_SEARCH_MAX_LENGTH * 0.8);

  return (
    <PageFiltersBar
      validationMessage={searchError}
      validationId="call-search-validation"
      validationEnd={
        showCharacterCount ? (
          <Text
            size="tiny"
            color={searchError ? 'danger' : 'secondary'}
            className="leading-5 tabular-nums"
          >
            {normalizedSearchLength} / {CALL_SEARCH_MAX_LENGTH}
          </Text>
        ) : undefined
      }
    >
      <div className="min-w-[260px] flex-1">
        <LabelTextInput
          // TODO: check when implementing i18n
          label="Buscar"
          labelPosition="top"
          size="md"
          inputProps={{
            // TODO: check when implementing i18n
            placeholder: 'Busca por número, contacto o agente',
            value: localSearch,
            'aria-label': 'Buscar',
            'aria-invalid': !!searchError,
            'aria-describedby': searchError
              ? 'call-search-validation'
              : undefined,
            onChange: (event) => setLocalSearch(event.target.value),
            onKeyDown: (event) => {
              if (event.key === 'Enter') {
                onSearchChange(localSearch);
              }
            },
          }}
        />
      </div>
      <div className="min-w-[200px] sm:w-auto">
        <LabelSelect
          // TODO: check when implementing i18n
          label={<Text size="small">Agente</Text>}
          options={agentOptions}
          selectProps={{
            value: agentId ? agentId.toString() : '',
            'aria-label': 'Agente',
            onChange: (event: ChangeEvent<HTMLSelectElement>) => {
              handleAgentChange(event.target.value);
            },
            loading: agentsLoading,
          }}
          labelPosition="top"
          variant="primary"
          size="md"
          className="w-full"
        />
      </div>
    </PageFiltersBar>
  );
};
